import * as React from 'react'
import {runInAction} from 'mobx'
import {observer} from 'mobx-react-lite'
import {IconButton, Typography} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import {LinkIcon, AddIcon, DeleteIcon} from '@oc/icons'
import {OcValidatedTextField} from './OcTextFields'
import {messages} from '../translations'

export const OcLinkInputRuleName = 'isValidLink'

export interface OcLinkInputListProps {
    canEdit: boolean
    links: string[]
    updateLink: (newValue: string, idx: number) => void
    deleteLink?: (idx: number) => void
    addLink: () => void
    title?: string
    labelPrefix?: string
    notValidMessage?: string
    placeholder?: string
}

export function ocLinkInputValidate(link: string): boolean {
    if (!link || link.match(/^(?:http(s)?:\/\/){1}(?:[\w-]+[.])+[\w]+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/)) {
        return true
    }
    return false
}

const useStyles = makeOcStyles(theme => ({
    title: {
        display: 'flex',
        alignItems: 'center'
    },
    titleTypo: {
        width: 'calc(100% - 24px - 24px - 20px)',
        paddingLeft: theme.spacing(2)
    },
    linkList: {
        display: 'flex',
        alignItems: 'start',
        marginBottom: theme.spacing(2),
        width: '100%'
    },
    linkInput: {
        width: 'calc(100% - 24px - 20px)'
    },
    button: {
        padding: '10px'
    }
}))

function _OcLinkInputList(props: OcLinkInputListProps) {
    const {links, addLink, updateLink, title, labelPrefix, notValidMessage, placeholder, canEdit, deleteLink} = props
    const {classes} = useStyles()

    const onChange = React.useCallback(
        (value: string, index: number) => {
            runInAction(() => {
                links[index] = value
                updateLink(value, index)
            })
        },
        [links, updateLink]
    )

    const deleteLinkCallback = React.useCallback(
        (index: number) => {
            runInAction(() => {
                if (deleteLink) {
                    deleteLink(index)
                }
            })
        },
        [deleteLink]
    )

    const openLink = React.useCallback((link: string) => {
        window.open(link, '_blank')
    }, [])

    return (
        <div>
            <div className={classes.title}>
                <LinkIcon size="24px" />
                <Typography variant="subtitle2" className={classes.titleTypo}>
                    {title || messages.getText('links', 'links')}
                </Typography>
                {canEdit ? (
                    <IconButton onClick={addLink} className={classes.button} size="large">
                        <AddIcon size="24px" />
                    </IconButton>
                ) : undefined}
            </div>
            {links.map((link, idx) => (
                <div className={classes.linkList} key={idx}>
                    <div className={classes.linkInput}>
                        <OcValidatedTextField
                            id={idx.toString()}
                            label={`${labelPrefix || messages.getText('links', 'link')} ${idx + 1}`}
                            name={`link_${idx + 1}`}
                            value={link}
                            validators={['isValidLink']}
                            errorMessages={[notValidMessage || messages.getText('links', 'notValid')]}
                            onChange={e => onChange(e.target.value, idx)}
                            placeholder={placeholder || messages.getText('links', 'placeholder')}
                            disabled={!canEdit}
                        />
                    </div>
                    <IconButton onClick={() => deleteLinkCallback(idx)} className={classes.button} size="large">
                        <DeleteIcon size="24px" />
                    </IconButton>
                    <IconButton
                        onClick={() => openLink(link)}
                        className={classes.button}
                        disabled={!ocLinkInputValidate(link)}
                        size="large"
                    >
                        <LinkIcon size="24px" />
                    </IconButton>
                </div>
            ))}
        </div>
    )
}

export const OcLinkInputList = observer(_OcLinkInputList)
